import React, { useContext, useEffect, useState } from "react";
import Tabs from "./Tabs";
import { getSingleCheckboxCheckedTimesheet } from "store/selector/rostering.selector";
import { useDispatch, useSelector } from "react-redux";
import { apiGetRequest } from "helpers/Requests";
import { URL_ROSTERING_GET_ALL_DETAILS_OF_TIMESHEET } from "store/services/URL";
import {
  HiOutlineArrowUturnLeft,
  HiOutlineCheck,
  HiOutlineChevronDoubleRight,
  HiOutlineInformationCircle,
  HiOutlineXMark,
} from "react-icons/hi2";
import { LoginContext } from "helpers/LoginContext";
import { singleCheckboxSelectedInTimesheet } from "store/actions/rostering.actions";
import TimesheetDetailHeader from "../../components/TimesheetRevamp/TimesheetDetailHeader";
import "../../components/TimesheetRevamp/timesheet-detail-revamp.css";
import {
  buildTimesheetConfirmDetails,
  hasScheduleActualMismatch,
} from "../../utils/timesheetConfirmDetails";

const TabSection = ({
  handleStatusChange,
  requestApproveConfirmation,
  timesheetsData = [],
  setCheckboxes = [],
  loadingStatus,
  refresh,
  navigateToTabId,
  onTabNavigateConsumed,
}) => {
  const singleTimesheet = useSelector(getSingleCheckboxCheckedTimesheet);

  const approvalTargets = () =>
    singleTimesheet?.uuid ? [singleTimesheet] : [];

  const dispatch = useDispatch();
  const { isUserGracePeriodOver } = useContext(LoginContext);

  useEffect(() => {
    if (singleTimesheet?.uuid) {
      getShiftDetails();
    }
  }, [singleTimesheet?.uuid, refresh]);
  const [shiftDetailsLoader, setShiftDetailsLoader] = useState(false);
  const [shiftDetails, setShiftDetails] = useState(null);

  const getShiftDetails = (options = {}) => {
    const silent = options.silent === true;
    if (!silent) {
      setShiftDetailsLoader(true);
    }
    apiGetRequest(
      `${URL_ROSTERING_GET_ALL_DETAILS_OF_TIMESHEET}/${singleTimesheet?.uuid}`
    )
      .then((res) => {
        const data = res?.data?.data ?? res?.data;
        setShiftDetails(data);
      })
      .catch((err) => {
        console.log("Error fetching shift details:", err);
      })
      .finally(() => {
        if (!silent) {
          setShiftDetailsLoader(false);
        }
      });
  };

  const advanceToNextTimesheet = () => {
    const currentIndex = timesheetsData?.findIndex(
      (timesheet) => timesheet.uuid === singleTimesheet?.uuid
    );

    let nextIndex = currentIndex + 1;

    while (
      nextIndex < timesheetsData.length &&
      (timesheetsData[nextIndex]?.status === "In-progress" ||
        timesheetsData[nextIndex]?.status === "N/A")
    ) {
      nextIndex++;
    }

    if (nextIndex < timesheetsData.length) {
      dispatch(singleCheckboxSelectedInTimesheet(timesheetsData[nextIndex]));
    }
  };

  const confirmAndApprove = async (approveNext = false) => {
    const summaryRow = shiftDetails || singleTimesheet;
    const confirmed = await requestApproveConfirmation?.({
      scheduleMismatch: hasScheduleActualMismatch(summaryRow),
      details: buildTimesheetConfirmDetails(summaryRow),
    });

    if (!confirmed) return;

    const approvedTimePreference =
      shiftDetails?.time_display_preference === "scheduled" ||
      shiftDetails?.time_display_preference === "actual"
        ? shiftDetails.time_display_preference
        : undefined;

    handleStatusChange(approvalTargets(), "approved", true, approveNext, {
      approved_time_preference: approvedTimePreference,
    });

    if (approveNext) {
      advanceToNextTimesheet();
    }
  };

  const handleApproveNext = () => {
    confirmAndApprove(true);
  };

  const handleUnpproved = () => {
    handleStatusChange(approvalTargets(), "pending", true, false);
  };

  const handleApproveTimesheet = () => {
    confirmAndApprove(false);
  };

  const handleCloseDetail = () => {
    setCheckboxes([]);
    dispatch(singleCheckboxSelectedInTimesheet(null));
  };

  const isDisabledApproveButton =
    shiftDetails?.status === "approved" ||
    shiftDetails?.status === "In-progress" ||
    shiftDetails?.status === "N/A";
  const isDisabled =
    shiftDetails?.status === "In-progress" ||
    shiftDetails?.status === "N/A" ||
    shiftDetails?.status === "rejected" ||
    shiftDetails?.status === "approved" ||
    shiftDetails?.status === "Expired";

  const scheduleMismatch =
    shiftDetails &&
    (shiftDetails.start_time !== shiftDetails.worker_start_time ||
      shiftDetails.end_time !== shiftDetails.worker_end_time);

  const approvalLoading = loadingStatus[singleTimesheet?.uuid] || false;

  return (
    <div
      className="timesheet-detail-panel flex h-[min(640px,62vh)] max-h-[705px] flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_-2px_24px_rgba(15,23,42,0.06)] max-lg:max-h-[min(720px,85vh)]"
    >
      <div className="h-[3px] w-full shrink-0 bg-[#4A6BB6]" />
      <div className="shrink-0">
        <TimesheetDetailHeader
          shiftDetails={shiftDetails}
          workerFallback={singleTimesheet?.worker}
          onClose={handleCloseDetail}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Tabs
          setShiftDetails={setShiftDetails}
          shiftDetails={shiftDetails}
          selectedTimesheet={singleTimesheet}
          shiftDetailsLoader={shiftDetailsLoader}
          isDisabledButtons={isDisabled}
          navigateToTabId={navigateToTabId}
          onTabNavigateConsumed={onTabNavigateConsumed}
          onCodeAllocationSaved={() => getShiftDetails({ silent: true })}
        />
      </div>

      <div
        className="timesheet-approval-bar shrink-0 flex w-full flex-col gap-3 border-t border-[#E5E7EB] bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
      >
        <div>
          <label className="text-[13px] font-bold text-[#0F172A]">
            Timesheet approval
          </label>
          {shiftDetails?.roster_invoice_generated ? (
            <p className="td-approval-helper mt-0.5">
              <HiOutlineInformationCircle className="h-3.5 w-3.5 shrink-0" />
              {shiftDetails?.invoice_number
                ? `Invoice ${shiftDetails.invoice_number} is generated. Unapproving will not void it.`
                : "An invoice is generated. Unapproving will not void it."}
            </p>
          ) : scheduleMismatch ? (
            <p className="td-approval-helper mt-0.5">
              <HiOutlineInformationCircle className="h-3.5 w-3.5 shrink-0" />
              Schedule and actual time differ — confirm before approving
            </p>
          ) : (
            <p className="text-xs text-[#94A3B8]">
              Review and confirm this shift before invoicing
            </p>
          )}
        </div>
        <div className="flex min-w-0 flex-wrap items-stretch justify-end gap-2 sm:items-center sm:pr-14 [&>button]:min-w-0 sm:[&>button]:min-w-[auto]">
          {shiftDetails ? (
            shiftDetails?.status === "approved" ||
            shiftDetails?.status === "Expired" ||
            shiftDetails?.status === "rejected" ? (
              <button
                type="button"
                className="td-approval-btn td-approval-btn--unapprove"
                disabled={approvalLoading || isUserGracePeriodOver}
                onClick={() => {
                  if (isUserGracePeriodOver) return;
                  handleUnpproved();
                }}
              >
                {approvalLoading ? (
                  "Loading..."
                ) : (
                  <>
                    <HiOutlineArrowUturnLeft className="h-4 w-4 shrink-0" />
                    Unapprove
                  </>
                )}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="td-approval-btn td-approval-btn--reject"
                  disabled={
                    shiftDetails?.status === "rejected" ||
                    shiftDetails?.status === "N/A" ||
                    shiftDetails?.status === "In-progress" ||
                    approvalLoading
                  }
                  onClick={() =>
                    handleStatusChange(approvalTargets(), "rejected", true)
                  }
                >
                  {approvalLoading ? (
                    "Loading..."
                  ) : (
                    <>
                      <HiOutlineXMark className="h-4 w-4" />
                      Reject Timesheet
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="td-approval-btn td-approval-btn--approve-next"
                  disabled={isDisabledApproveButton || approvalLoading}
                  onClick={() => {
                    if (isUserGracePeriodOver) return;
                    handleApproveNext();
                  }}
                >
                  {approvalLoading ? (
                    "Loading..."
                  ) : (
                    <>
                      <HiOutlineChevronDoubleRight className="h-4 w-4" />
                      Approve & Next
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="td-approval-btn td-approval-btn--approve"
                  disabled={isDisabledApproveButton || approvalLoading}
                  onClick={() => {
                    if (isUserGracePeriodOver) return;
                    handleApproveTimesheet();
                  }}
                >
                  {approvalLoading ? (
                    "Loading..."
                  ) : (
                    <>
                      <HiOutlineCheck className="h-4 w-4" />
                      Approve
                    </>
                  )}
                </button>
              </>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TabSection;
